import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Button, Card, Col, Form, InputGroup, Modal, Row, Table, Badge,
} from "react-bootstrap";
import debounce from "lodash/debounce";
import { useNavigate, useParams } from "react-router-dom";
import UserContext from '../providers/UserContext.js';

// ===== Generated API clients & DTOs =====
import {
  CategorysClient,
  InspectionItemsClient,
  InspectionsClient,
  VehiclesClient,
  CategoryDto,
  InspectionItemDto,
  CreateVehicleCommand,
  CreateInspectionCommand,
  UpdateInspectionCommand,
  InspectionItemRecordDto,
  FileParameter,
} from "../web-api-client.ts";
import NewVehicle from "./NewVehicle.js";
import QR from "./QR.js";
import LoaderCover from "./LoaderCover.js";
import { isMobileDevice } from "./utils/utils.js";

// ===== Types (UI) =====
type ID = string;

type Vehicle = {
  id: ID;
  vin: string;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  stockNumber?: string | null;
  trim?: string | null;
  mileage?: number | null;
  color?: string | null;
  thumbnailUrl?: string | null;
  photoUri?: string | null;
};

type InspectionItem = {
  id: ID;
  order: number;
  title: string;
  categoryId: number;
  description?: string | null;
  allowsNote?: boolean;
  allowsPhotos?: boolean; // also governs videos (media)
  statusLabelGreen?: string;
  statusLabelYellow?: string;
  statusLabelRed?: string;
};

type ItemStatus = "green" | "yellow" | "red" | "na";

// ---- Media shapes ----
// Photos
type NewPhoto = { tempId: string; file: File; url?: string; isNew: true };
type SavedPhoto = { id: ID; url: string; thumbnailUrl?: string; isNew?: false };
type Photo = NewPhoto | SavedPhoto;

// Videos
type NewVideo = { tempId: string; file: File; url?: string; isNew: true };
type SavedVideo = { id: ID; url: string; thumbnailUrl?: string; contentType?: string; isNew?: false };
type Video = NewVideo | SavedVideo;

type InspectionItemRecord = {
  inspectionItemId: ID;
  status?: ItemStatus;
  note?: string;
  photos?: Photo[];
  videos?: Video[]; // NEW
};

type Inspection = {
  id?: ID;
  vehicleId?: ID;
  vehicle?: Vehicle | null;
  savedAt?: string | null;
  publishedAt?: string | null;
  records: Record<ID, InspectionItemRecord>; // keyed by inspectionItemId
  // --- Vehicle history report (PDF) ---
  vehicleHistoryReportUrl?: string | null;
  vehicleHistoryReportUploaded?: string | null; // ISO string
  vehicleHistoryReportUploadedBy?: string | null;
  // --- Vehicle service record (PDF) ---
  vehicleServiceRecordUrl?: string | null;
  vehicleServiceRecordUploaded?: string | null; // ISO string
  vehicleServiceRecordUploadedBy?: string | null;
  notes?: string | null;
  dealerWebsiteLink?: string | null;
  status: number;
  scanCount: number;
};

// ===== Status mapping =====
const statusMapToServer: Record<ItemStatus, number> = {
  green: 0,
  yellow: 1,
  red: 2,
  na: 3,
};
const statusMapFromServer = (n?: number): ItemStatus | undefined => {
  switch (n) {
    case 0: return "green";
    case 1: return "yellow";
    case 2: return "red";
    case 3: return "na";
    default: return undefined;
  }
};

// ===== Small helpers =====
const statusMeta: Record<ItemStatus, { label: string; bg: string; title: string }> = {
  green: { label: "●", bg: "success", title: "Good" },
  yellow: { label: "●", bg: "warning", title: "Fair" },
  red: { label: "●", bg: "danger", title: "Poor" },
  na: { label: "●", bg: "secondary", title: "N/A" },
};

function ensureRecords(items: InspectionItem[], current: Inspection["records"]): Inspection["records"] {
  const copy = { ...current };
  for (const item of items) {
    if (!copy[item.id]) {
      copy[item.id] = { inspectionItemId: item.id, photos: [], videos: [] };
    } else {
      if (!copy[item.id].photos) copy[item.id].photos = [];
      if (!copy[item.id].videos) copy[item.id].videos = [];
    }
  }
  return copy;
}

// ===== API wiring (categories/items) =====
async function fetchCategories(dealerId:number): Promise<CategoryDto[]> {
  const client = new CategorysClient();
  const cats = await client.getCategoryList(dealerId);
  return cats.filter(c => (c.name ?? "").toLowerCase() !== "inspections");
}

async function fetchInspectionItems(dealerId:number): Promise<InspectionItem[]> {
  const client = new InspectionItemsClient();
  const raw = await client.getInspectionItemList(dealerId, undefined);

  const byCat = new Map<number, InspectionItemDto[]>();
  for (const it of raw) {
    if (!it.categoryId) continue;
    if (!byCat.has(it.categoryId)) byCat.set(it.categoryId, []);
    byCat.get(it.categoryId)!.push(it);
  }

  const uiItems: InspectionItem[] = [];
  for (const [categoryId, arr] of byCat.entries()) {
    const sorted = [...arr].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    sorted.forEach((dto, idx) => {
      uiItems.push({
        id: String(dto.id!),
        order: idx + 1,
        title: dto.name ?? `Item ${dto.id}`,
        categoryId,
        description: dto.description ?? undefined,
        allowsNote: true,
        allowsPhotos: !!dto.hasPhoto,
        statusLabelGreen: dto.statusLabelGreen ?? 'Good',
        statusLabelYellow: dto.statusLabelYellow ?? 'Fair',
        statusLabelRed: dto.statusLabelRed ?? 'Poor',
      });
    });
  }
  return uiItems;
}

// ===== Vehicles =====
async function searchVehicles(dealerId:number, query: { vin?: string; q?: string }): Promise<Vehicle[]> {
  const vc = new VehiclesClient();
  const search = query.vin?.trim() ? query.vin.trim() : (query.q?.trim() ?? "");
  const page = await vc.getVehicleList(
    dealerId, 1, 10,
    search || undefined, undefined, undefined, undefined, undefined, undefined, undefined
  );
  return (page.items ?? []).map<Vehicle>((v) => ({
    id: String(v.id!),
    vin: v.vin ?? "",
    year: v.year ? Number(v.year) : undefined,
    make: v.makeName ?? undefined,
    model: v.modelName ?? undefined,
    photoUri: (v as any).photoUri ?? (v as any).thumbnailUrl ?? null,
  }));
}

async function createVehicle(dealerId:number , input: Omit<Vehicle, "id">): Promise<Vehicle> {
  const vc = new VehiclesClient();
  const cmd = new CreateVehicleCommand({
    dealerId: dealerId,
    vin: input.vin,
    year: input.year ? String(input.year) : undefined,
    makeId: undefined,
    modelId: undefined,
    stockNumber: input.stockNumber ? input.stockNumber : undefined,
    mileage: input.mileage ? input.mileage : undefined,
    trim: input.trim ? input.trim : undefined,
    color: input.color ? input.color : undefined,
    status: 0,
  });
  const dto = await vc.createVehicle(cmd);
  return {
    id: String(dto.id!),
    vin: dto.vin ?? "",
    year: dto.year ? Number(dto.year) : undefined,
    make: dto.makeName ?? undefined,
    model: dto.modelName ?? undefined,
    stockNumber: dto.stockNumber ?? undefined,
    trim: dto.trim ?? undefined,
    mileage: dto.mileage ?? undefined,
    color: dto.color ?? undefined,
  };
}

// ===== Inspection create/update commands (no media; media are separate API) =====
function toCreateInspectionCommand(
  dealerId: number,
  vehicleId: number,
  records: Record<ID, InspectionItemRecord>,
  notes?: string | null,
  dealerWebsiteLink?: string | null,
  status: number = 0,
): CreateInspectionCommand {
  const cmd = new CreateInspectionCommand({
    dealerId: dealerId,
    vehicleId,
    status: status ?? 0,
    inspectionDate: new Date(),
    inspectorName: undefined,
    notes: notes ?? undefined,
    dealerWebsiteLink: dealerWebsiteLink ?? undefined,
    inspectionItemRecords: [],
  });

  const list: InspectionItemRecordDto[] = [];
  Object.values(records).forEach(r => {
    const iid = Number(r.inspectionItemId);
    const dto = new InspectionItemRecordDto({
      inspectionItemId: iid,
      status: r.status ? statusMapToServer[r.status] : undefined,
      note: r.note,
      recommendation: undefined,
      photoUri: undefined,
    });
    list.push(dto);
  });
  cmd.inspectionItemRecords = list;
  return cmd;
}

function toUpdateInspectionCommand(
  dealerId: number,
  id: number,
  vehicleId: number,
  records: Record<ID, InspectionItemRecord>,
  notes?: string | null,
  dealerWebsiteLink?: string | null,
  status: number = 0
): UpdateInspectionCommand {
  const cmd = new UpdateInspectionCommand({
    id,
    dealerId: dealerId,
    vehicleId,
    status: status ?? 0,
    inspectionDate: new Date(),
    inspectorName: undefined,
    notes: notes ?? undefined,
    dealerWebsiteLink: dealerWebsiteLink ?? undefined,
    inspectionRecords: [],
  });

  const list: InspectionItemRecordDto[] = [];
  Object.values(records).forEach(r => {
    const iid = Number(r.inspectionItemId);
    const dto = new InspectionItemRecordDto({
      inspectionItemId: iid,
      status: r.status ? statusMapToServer[r.status] : undefined,
      note: r.note,
      recommendation: undefined,
      photoUri: undefined,
    });
    list.push(dto);
  });
  cmd.inspectionRecords = list;
  return cmd;
}

// ===== Vehicle Picker =====
const VehiclePicker: React.FC<{
  value?: Vehicle | null;
  onChange: (v: Vehicle | null) => void;
}> = ({ value, onChange }) => {
  const [vin, setVin] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Vehicle[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const closeAddVehicleModal = () => setShowAddVehicleModal(false);
  const openAddVehicleModal = () => setShowAddVehicleModal(true);
  const user = React.useContext(UserContext);

  const [creating, setCreating] = useState<Omit<Vehicle, "id">>({
    vin: "",
    make: "",
    model: "",
    year: undefined,
  });

  const runSearch = useCallback(
    debounce(async (vinVal: string, qVal: string) => {
      const r = await searchVehicles(user?.dealerId,{ vin: vinVal || undefined, q: qVal || undefined });
      setResults(r);
    }, 350),
    []
  );

  useEffect(() => {
    if (!value) runSearch(vin, q);
  }, [vin, q, value, runSearch]);

  // mobile VIN middle-ellipsis helper
  const middleEllipsis = (text: string, keep: number = 6) => {
    if (!text) return "";
    if (text.length <= keep * 2 + 1) return text;
    return `${text.slice(0, keep)}…${text.slice(-keep)}`;
  };

  if (value) {
    return (
      <>
        <Card className="mb-3">
          <Card.Body className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              {value.photoUri ? (
                <img
                  src={value.photoUri}
                  alt="Vehicle"
                  style={{ width: 120, height: 90, objectFit: "cover", borderRadius: 8, border: "1px solid rgba(0,0,0,0.1)" }}
                />
              ) : (
                <div
                  style={{
                    width: 120,
                    height: 90,
                    borderRadius: 8,
                    border: "1px dashed rgba(0,0,0,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    color: "#888",
                  }}
                  aria-label="No vehicle image"
                  title="No vehicle image"
                >
                  No image
                </div>
              )}
              <div className="h5 mb-0">
                <Badge bg="info" className="me-2">Selected</Badge>
                {value.year ?? "-"} {value.make ?? ""} {value.model ?? ""} • {value.vin}
              </div>
            </div>

            <div className="d-flex gap-2">
              <Button variant="outline-danger" onClick={() => onChange(null)}>
                Change Vehicle
              </Button>
            </div>
          </Card.Body>
        </Card>

        <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
          <Modal.Header closeButton><Modal.Title>Add Vehicle</Modal.Title></Modal.Header>
          <Modal.Body>
            <Row className="g-3">
              <Col md={12}>
                <Form.Label>VIN</Form.Label>
                <Form.Control
                  value={creating.vin}
                  onChange={(e) => setCreating({ ...creating, vin: e.target.value })}
                  placeholder="VIN"
                />
              </Col>
              <Col md={4}>
                <Form.Label>Year</Form.Label>
                <Form.Control
                  type="number"
                  value={creating.year ?? ""}
                  onChange={(e) =>
                    setCreating({ ...creating, year: e.target.value ? Number(e.target.value) : undefined })
                  }
                />
              </Col>
              <Col md={4}>
                <Form.Label>Make</Form.Label>
                <Form.Control
                  value={creating.make ?? ""}
                  onChange={(e) => setCreating({ ...creating, make: e.target.value })}
                />
              </Col>
              <Col md={4}>
                <Form.Label>Model</Form.Label>
                <Form.Control
                  value={creating.model ?? ""}
                  onChange={(e) => setCreating({ ...creating, model: e.target.value })}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                const v = await createVehicle(user.dealerId, creating);
                onChange(v);
                setShowCreate(false);
                setCreating({ vin: "", make: "", model: "", year: undefined });
              }}
            >
              Save Vehicle
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showAddVehicleModal} onHide={closeAddVehicleModal} size="lg" scrollable centered backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>New Vehicle</Modal.Title>
          </Modal.Header>
          <NewVehicle onComplete={closeAddVehicleModal} />
        </Modal>
      </>
    );
  }

  // Search UI
  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <h5 className="mb-3">Vehicle</h5>
          <Row className="g-3 align-items-end">
            <Col md={7}>
              <Form.Label>Search</Form.Label>
              <Form.Control
                placeholder="Search by VIN, make, model…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </Col>
            <Col md={5} className="d-flex gap-2">
              <Button
                className="w-100 w-md-auto"
                variant="secondary"
                onClick={openAddVehicleModal}
                title="Add a new vehicle"
              >
                Add New Vehicle
              </Button>
            </Col>
          </Row>

          <div className="mt-3">
            {/* DESKTOP (md+) TABLE VIEW */}
            <div className="d-none d-md-block">
              {results.length === 0 ? (
                <div className="text-muted">No matches.</div>
              ) : (
                <Table hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>VIN</th><th>Year</th><th>Make</th><th>Model</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map(r => (
                      <tr key={r.id}>
                        <td className="font-monospace">{r.vin}</td>
                        <td>{r.year ?? "-"}</td>
                        <td>{r.make ?? "-"}</td>
                        <td>{r.model ?? "-"}</td>
                        <td className="text-end">
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => onChange(r)}
                          >
                            Select
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>

            {/* MOBILE (sm and down) CARD LIST */}
            <div className="d-block d-md-none">
              {results.length === 0 ? (
                <div className="text-muted">No matches.</div>
              ) : (
                <ul className="list-unstyled m-0">
                  {results.map(r => (
                    <li key={r.id} className="mb-2">
                      <div className="d-grid" style={{ gridTemplateColumns: "1fr auto", gap: ".75rem" }}>
                        <div className="p-3 rounded border" style={{ background: "var(--bs-card-bg, rgba(255,255,255,0.03))" }}>
                          <div className="fw-semibold d-flex align-items-center flex-wrap">
                            <span className="me-1">{r.make ?? "-"}</span>
                            <span>{r.model ?? "-"}</span>
                            {r.year && <span className="badge bg-secondary ms-2">{r.year}</span>}
                          </div>
                          <div className="mt-1 d-flex align-items-center">
                            <code className="font-monospace">{middleEllipsis(r.vin)}</code>
                            <Button
                              variant="link"
                              size="sm"
                              className="ps-2 pe-0"
                              onClick={() => navigator.clipboard?.writeText(r.vin || "")}
                              title="Copy VIN"
                              aria-label="Copy VIN"
                            >
                              Copy
                            </Button>
                          </div>
                        </div>
                        <div className="d-flex align-items-stretch">
                          <Button
                            size="sm"
                            className="px-3"
                            variant="primary"
                            onClick={() => onChange(r)}
                          >
                            Select
                          </Button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton><Modal.Title>Add Vehicle</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={12}>
              <Form.Label>VIN</Form.Label>
              <Form.Control
                value={creating.vin}
                onChange={(e) => setCreating({ ...creating, vin: e.target.value })}
                placeholder="VIN"
              />
            </Col>
            <Col md={4}>
              <Form.Label>Year</Form.Label>
              <Form.Control
                type="number"
                value={creating.year ?? ""}
                onChange={(e) =>
                  setCreating({ ...creating, year: e.target.value ? Number(e.target.value) : undefined })
                }
              />
            </Col>
            <Col md={4}>
              <Form.Label>Make</Form.Label>
              <Form.Control
                value={creating.make ?? ""}
                onChange={(e) => setCreating({ ...creating, make: e.target.value })}
              />
            </Col>
            <Col md={4}>
              <Form.Label>Model</Form.Label>
              <Form.Control
                value={creating.model ?? ""}
                onChange={(e) => setCreating({ ...creating, model: e.target.value })}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              const v = await createVehicle(user.dealerId, creating);
              onChange(v);
              setShowCreate(false);
              setCreating({ vin: "", make: "", model: "", year: undefined });
            }}
          >
            Save Vehicle
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAddVehicleModal} onHide={closeAddVehicleModal} size="lg" scrollable centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>New Vehicle</Modal.Title>
        </Modal.Header>
        <NewVehicle onComplete={closeAddVehicleModal} />
      </Modal>
    </>
  );
};

// ===== Status Selector =====
const StatusPills: React.FC<{
  value?: ItemStatus;
  statusLabelGreen?: string;
  statusLabelYellow?: string;
  statusLabelRed?: string;
  onChange: (s: ItemStatus) => void;
}> = ({ value, onChange, statusLabelGreen, statusLabelYellow, statusLabelRed }) => {
  return (
    <div className="d-flex align-items-center gap-3">
      {(["green", "yellow", "red", "na"] as ItemStatus[]).map((s) => (
        <Button
          key={s}
          size="sm"
          variant={value === s ? statusMeta[s].bg : "outline-" + statusMeta[s].bg}
          onClick={() => onChange(s)}
          title={statusMeta[s].title}
        >
          {s === "green" ? (statusLabelGreen || "Good") : s === "yellow" ? (statusLabelYellow || "Fair") : s === "red" ? (statusLabelRed || "Poor") : "N/A"}
        </Button>
      ))}
    </div>
  );
};

// ===== Item Row (now with Photos + Videos) =====
const ItemRow: React.FC<{
  item: InspectionItem;
  record: InspectionItemRecord;
  onChange: (patch: Partial<InspectionItemRecord>) => void;
  onDeleteSavedPhoto?: (photoId: string) => Promise<void>;
  onDeleteSavedVideo?: (videoId: string) => Promise<void>;
}> = ({ item, record, onChange, onDeleteSavedPhoto, onDeleteSavedVideo }) => {
  const photoInputRef = React.useRef<HTMLInputElement | null>(null);
  const videoInputRef = React.useRef<HTMLInputElement | null>(null);

  // --- Photos ---
  const onPhotoPicked = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const newOnes: NewPhoto[] = Array.from(files).map((f) => ({
      tempId: crypto.randomUUID(),
      file: f,
      url: URL.createObjectURL(f),
      isNew: true,
    }));
    onChange({ photos: [...(record.photos ?? []), ...newOnes] });
  };
  const openPhotoPicker = () => photoInputRef.current?.click();
  const onHiddenPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onPhotoPicked(e.target.files);
    e.target.value = "";
  };
  const removePhoto = async (idx: number) => {
    const list = [...(record.photos ?? [])];
    const removed = list[idx];

    if (!("isNew" in removed) && onDeleteSavedPhoto) {
      await onDeleteSavedPhoto((removed as SavedPhoto).id);
    }

    if ("isNew" in removed && removed.url) {
      try { URL.revokeObjectURL(removed.url); } catch { }
    }

    list.splice(idx, 1);
    onChange({ photos: list });
  };

  // --- Videos ---
  const onVideoPicked = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const acceptables = Array.from(files).filter(f => {
      const t = (f.type || "").toLowerCase();
      const ext = f.name.toLowerCase();
      return t.startsWith("video/") || ext.endsWith(".mp4") || ext.endsWith(".mov") || ext.endsWith(".webm") || ext.endsWith(".m4v");
    });
    const newOnes: NewVideo[] = acceptables.map((f) => ({
      tempId: crypto.randomUUID(),
      file: f,
      url: URL.createObjectURL(f),
      isNew: true,
    }));
    if (newOnes.length > 0) onChange({ videos: [...(record.videos ?? []), ...newOnes] });
  };
  const openVideoPicker = () => videoInputRef.current?.click();
  const onHiddenVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onVideoPicked(e.target.files);
    e.target.value = "";
  };
  const removeVideo = async (idx: number) => {
    const list = [...(record.videos ?? [])];
    const removed = list[idx];

    if (!("isNew" in removed) && onDeleteSavedVideo) {
      await onDeleteSavedVideo((removed as SavedVideo).id);
    }

    if ("isNew" in removed && removed.url) {
      try { URL.revokeObjectURL(removed.url); } catch { }
    }

    list.splice(idx, 1);
    onChange({ videos: list });
  };

  return (
    <Card className="mb-3">
      <Card.Body>
        <Row className="g-3 align-items-start">
          <Col md={6}>
            <div className="fw-semibold">{item.order}. {item.title}</div>
           {/*  {item.description && (
              <div className="text-muted small mt-1">{item.description}</div>
            )} */}
          </Col>
          <Col md={6} className="d-flex justify-content-md-end">
            <StatusPills
              value={record.status}
              statusLabelGreen={item.statusLabelGreen}
              statusLabelYellow={item.statusLabelYellow}
              statusLabelRed={item.statusLabelRed}
              onChange={(s) => onChange({ status: s })}
            />
          </Col>

          {item.allowsNote && (
            <Col md={12}>
              <Form.Label>Technician Note</Form.Label>
              <Form.Control
                placeholder="Add a note"
                value={record.note ?? ""}
                onChange={(e) => onChange({ note: e.target.value })}
              />
            </Col>
          )}

          {item.allowsPhotos && (
            <>
              {/* Photos */}
              <Col md={12}>
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <Form.Label className="mb-0">Photos</Form.Label>

                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={onHiddenPhotoChange}
                      style={{ display: "none" }}
                    />

                    <input
                      id={`camera-photo-${item.id}`}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => {
                        onPhotoPicked(e.target.files);
                        e.target.value = "";
                      }}
                      style={{ display: "none" }}
                    />

                    {isMobileDevice() && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => document.getElementById(`camera-photo-${item.id}`)?.click()}
                      >
                        📷 Take photo
                      </Button>
                    )}

                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={openPhotoPicker}
                    >
                      + Upload photos
                    </Button>
                  </div>

                  {(record.photos ?? []).length > 0 && (
                    <div className="d-flex flex-wrap gap-2">
                      {(record.photos ?? []).map((p, idx) => {
                        const key = "isNew" in p ? p.tempId : p.id;
                        const previewUrl = "isNew" in p ? p.url : p.thumbnailUrl ?? p.url;
                        const label = "isNew" in p ? "Pending upload" : `Photo ${idx + 1}`;
                        return (
                          <div key={key} className="border rounded p-2">
                            <div className="small text-muted mb-1">{label}</div>
                            {previewUrl ? (
                              <img
                                src={previewUrl}
                                alt=""
                                style={{ width: 96, height: 96, objectFit: "cover", display: "block" }}
                              />
                            ) : null}
                            <div className="mt-2 d-flex gap-2">
                              <Button size="sm" variant="outline-danger" onClick={() => removePhoto(idx)}>
                                Remove
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Col>

              {/* Videos */}
              <Col md={12}>
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <Form.Label className="mb-0">Videos</Form.Label>

                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/*,.mp4,.mov,.webm,.m4v"
                      multiple
                      onChange={onHiddenVideoChange}
                      style={{ display: "none" }}
                    />

                    <input
                      id={`camera-video-${item.id}`}
                      type="file"
                      accept="video/*"
                      capture="environment"
                      onChange={(e) => {
                        onVideoPicked(e.target.files);
                        e.target.value = "";
                      }}
                      style={{ display: "none" }}
                    />

                    {isMobileDevice() && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => document.getElementById(`camera-video-${item.id}`)?.click()}
                      >
                        🎥 Take video
                      </Button>
                    )}

                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={openVideoPicker}
                    >
                      + Upload videos
                    </Button>
                  </div>

                  {(record.videos ?? []).length > 0 && (
                    <div className="d-flex flex-wrap gap-2">
                      {(record.videos ?? []).map((v, idx) => {
                        const key = "isNew" in v ? v.tempId : v.id;
                        const srcUrl = "isNew" in v ? v.url : v.url; // saved uses url
                        const poster = ("isNew" in v ? undefined : v.thumbnailUrl) || undefined;
                        const label = "isNew" in v ? "Pending upload" : `Video ${idx + 1}`;
                        return (
                          <div key={key} className="border rounded p-2" style={{ width: 180 }}>
                            <div className="small text-muted mb-1">{label}</div>
                            {srcUrl ? (
                              <video
                                controls
                                playsInline
                                style={{ width: "100%", borderRadius: 6, display: "block" }}
                                poster={poster}
                              >
                                <source src={srcUrl} />
                                {/* Fallback text */}
                                Your browser does not support the video tag.
                              </video>
                            ) : null}
                            <div className="mt-2 d-flex gap-2">
                              <Button size="sm" variant="outline-danger" onClick={() => removeVideo(idx)}>
                                Remove
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Col>
            </>
          )}
        </Row>
      </Card.Body>
    </Card>
  );
};

// ===== Test helpers (randomize records) =====
function weightedRandomStatus(): ItemStatus {
  // ~60% green, 25% yellow, 10% red, 5% N/A
  const n = Math.random();
  if (n < 0.60) return "green";
  if (n < 0.85) return "yellow";
  if (n < 0.95) return "red";
  return "na";
}

function randomNoteFor(status: ItemStatus): string {
  const notes: Record<ItemStatus, string[]> = {
    green: ["Looks good", "No issues found", "Service not required", "OK today"],
    yellow: ["Monitor soon", "Recommend attention", "Slight wear noted", "Recheck next visit"],
    red: ["Needs repair", "Urgent attention required", "Unsafe condition", "Replace ASAP"],
    na: ["Not applicable", "Skipped for this vehicle", "Not equipped", "N/A"],
  };
  const arr = notes[status];
  return arr[Math.floor(Math.random() * arr.length)];
}

// ===== Main Editor =====
const DviEditor: React.FC = () => {
  const user = React.useContext(UserContext);
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const editingId = params.id ? Number(params.id) : undefined;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<CategoryDto[]>([]);
  const [items, setItems] = useState<InspectionItem[]>([]);
  const [inspection, setInspection] = useState<Inspection>({
    records: {},
    vehicle: null,
    status: 0,
    scanCount: 0,
  });
  const [resetBusy, setResetBusy] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const insClientRef = useRef<InspectionsClient | null>(null);
  if (!insClientRef.current) insClientRef.current = new InspectionsClient();
  const insClient = insClientRef.current;

  // QR state
  const [showQrModal, setShowQrModal] = useState(false);
  const qrInlineRef = useRef<HTMLDivElement | null>(null);
  const qrModalRef = useRef<HTMLDivElement | null>(null);

  const viewerUrl = useMemo(() => {
    return inspection.id ? `${window.location.origin}/dvi/view/${inspection.id}` : undefined;
  }, [inspection.id]);

  // --- Vehicle History Report (PDF) UI state ---
  const vhrInputRef = useRef<HTMLInputElement | null>(null);
  const [vhrBusy, setVhrBusy] = useState(false);
  const openVhrPicker = () => vhrInputRef.current?.click();

  const handleVhrFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = "";
    if (!file) return;
    if (!inspection.id) {
      alert("Save the inspection before uploading the vehicle history report.");
      return;
    }
    const isPdf = file.type?.toLowerCase().includes("pdf") || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      alert("Please select a PDF file.");
      return;
    }
    try {
      setVhrBusy(true);
      const fp: FileParameter = { data: file, fileName: file.name } as any;
      const result: any = await insClient.uploadVehicleHistoryReport(Number(inspection.id), fp);

      const url =
        result?.vehicleHistoryReportUrl ??
        result?.VehicleHistoryReportUrl ??
        result?.url ?? null;
      const uploaded =
        (result?.vehicleHistoryReportUploaded ?? result?.VehicleHistoryReportUploaded) ?
          new Date(result?.vehicleHistoryReportUploaded ?? result?.VehicleHistoryReportUploaded).toISOString() :
          new Date().toISOString();
      const by =
        result?.vehicleHistoryReportUploadedBy ??
        result?.VehicleHistoryReportUploadedBy ??
        null;

      setInspection(prev => ({
        ...prev,
        vehicleHistoryReportUrl: url,
        vehicleHistoryReportUploaded: uploaded,
        vehicleHistoryReportUploadedBy: by,
      }));
    } catch (err: any) {
      console.error(err);
      alert("Upload failed.");
    } finally {
      setVhrBusy(false);
    }
  };

  const handleVhrDelete = async () => {
    if (!inspection.id) return;
    if (!window.confirm("Remove the current vehicle history report?")) return;
    try {
      setVhrBusy(true);
      await insClient.deleteVehicleHistoryReport(Number(inspection.id));
      setInspection(prev => ({
        ...prev,
        vehicleHistoryReportUrl: null,
        vehicleHistoryReportUploaded: null,
        vehicleHistoryReportUploadedBy: null,
      }));
    } catch (err) {
      console.error(err);
      alert("Failed to remove report.");
    } finally {
      setVhrBusy(false);
    }
  };

  // --- Vehicle Service Record (PDF) UI state ---
  const vsrInputRef = useRef<HTMLInputElement | null>(null);
  const [vsrBusy, setVsrBusy] = useState(false);
  const openVsrPicker = () => vsrInputRef.current?.click();

  const handleVsrFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = "";
    if (!file) return;
    if (!inspection.id) {
      alert("Save the inspection before uploading the vehicle service record.");
      return;
    }
    const isPdf = file.type?.toLowerCase().includes("pdf") || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      alert("Please select a PDF file.");
      return;
    }
    try {
      setVsrBusy(true);
      const fp: FileParameter = { data: file, fileName: file.name } as any;
      const result: any = await insClient.uploadVehicleServiceRecord(Number(inspection.id), fp);

      const url =
        result?.vehicleServiceRecordUrl ??
        result?.VehicleServiceRecordUrl ??
        result?.url ?? null;
      const uploaded =
        (result?.vehicleServiceRecordUploaded ?? result?.VehicleServiceRecordUploaded) ?
          new Date(result?.vehicleServiceRecordUploaded ?? result?.VehicleServiceRecordUploaded).toISOString() :
          new Date().toISOString();
      const by =
        result?.vehicleServiceRecordUploadedBy ??
        result?.VehicleServiceRecordUploadedBy ??
        null;

      setInspection(prev => ({
        ...prev,
        vehicleServiceRecordUrl: url,
        vehicleServiceRecordUploaded: uploaded,
        vehicleServiceRecordUploadedBy: by,
      }));
    } catch (err: any) {
      console.error(err);
      alert("Upload failed.");
    } finally {
      setVsrBusy(false);
    }
  };

  const handleVsrDelete = async () => {
    if (!inspection.id) return;
    if (!window.confirm("Remove the current vehicle service record?")) return;
    try {
      setVsrBusy(true);
      await insClient.deleteVehicleServiceRecord(Number(inspection.id));
      setInspection(prev => ({
        ...prev,
        vehicleServiceRecordUrl: null,
        vehicleServiceRecordUploaded: null,
        vehicleServiceRecordUploadedBy: null,
      }));
    } catch (err) {
      console.error(err);
      alert("Failed to remove report.");
    } finally {
      setVsrBusy(false);
    }
  };

  // Load initial data (categories + items + existing inspection if editing)
  useEffect(() => {
    (async () => {
      setLoading(true);

      try {
        // 1) Taxonomy (categories + master items)
        const [cats, uiItems] = await Promise.all([
          fetchCategories(user?.dealerId),
          fetchInspectionItems(user?.dealerId),
        ]);

        const sortedCats = [...cats].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        const sortedItems = [...uiItems].sort((a, b) =>
          a.categoryId === b.categoryId ? a.order - b.order : a.categoryId - b.categoryId
        );

        setCategories(sortedCats);
        setItems(sortedItems);

        // 2) If editing, load inspection + vehicle + media
        if (editingId) {
          const dto = await insClient.getInspectionById(editingId);

          // vehicle hydrate
          let vehicle: Vehicle | null = null;
          if (dto.vehicleId) {
            try {
              const v = await new VehiclesClient().getVehicleById(dto.vehicleId);
              vehicle = {
                id: String(v.id!),
                vin: v.vin ?? "",
                year: v.year ? Number(v.year) : undefined,
                make: v.makeName ?? undefined,
                model: v.modelName ?? undefined,
                photoUri: (v as any).photoUri ?? (v as any).thumbnailUrl ?? null,
              };
            } catch {
              vehicle = {
                id: String(dto.vehicleId),
                vin: (dto as any).vehicleVin ?? "",
              } as Vehicle;
            }
          }

          const base: Inspection = {
            id: String(editingId),
            vehicleId: dto.vehicleId ? String(dto.vehicleId) : undefined,
            vehicle,
            savedAt: dto.created ? new Date(dto.created as any).toISOString() : null,
            publishedAt: undefined,
            records: {},
            vehicleHistoryReportUrl: (dto as any).vehicleHistoryReportUrl ?? null,
            vehicleHistoryReportUploaded: (dto as any).vehicleHistoryReportUploaded
              ? new Date((dto as any).vehicleHistoryReportUploaded).toISOString()
              : null,
            vehicleHistoryReportUploadedBy: (dto as any).vehicleHistoryReportUploadedBy ?? null,
            vehicleServiceRecordUrl: (dto as any).vehicleServiceRecordUrl ?? null,
            vehicleServiceRecordUploaded: (dto as any).vehicleServiceRecordUploaded
              ? new Date((dto as any).vehicleServiceRecordUploaded).toISOString()
              : null,
            vehicleServiceRecordUploadedBy: (dto as any).vehicleServiceRecordUploadedBy ?? null,
            notes: (dto as any).notes ?? null,
            dealerWebsiteLink: (dto as any).dealerWebsiteLink ?? null,
            status: dto.status ?? 0,
            scanCount: dto.scanCount ?? 0,
          };

          // records (no media here)
          (dto.inspectionItemRecords ?? []).forEach(r => {
            const iid = String(r.inspectionItemId!);
            base.records[iid] = {
              inspectionItemId: iid,
              status: statusMapFromServer(r.status),
              note: r.note ?? undefined,
              photos: [],
              videos: [],
            };
          });

          // ensure all item records exist
          const ensured = ensureRecords(sortedItems, base.records);

          // hydrate PHOTOS
          await Promise.all(
            sortedItems.map(async (it) => {
              const rec = ensured[it.id];
              if (!rec) return;
              try {
                const list = await insClient.getInspectionItemPhotosById(editingId, Number(it.id));
                const saved = (list ?? []).map(p => ({
                  id: String(p.id!),
                  url: p.url!,
                  thumbnailUrl: p.thumbnailUrl ?? undefined,
                })) as SavedPhoto[];
                rec.photos = saved;
              } catch { /* ignore */ }
            })
          );

          // hydrate VIDEOS
          await Promise.all(
            sortedItems.map(async (it) => {
              const rec = ensured[it.id];
              if (!rec) return;
              try {
                // requires server route mapped: GET /{inspectionId}/records/{inspectionItemId}/videos
                const vids = await (insClient as any).getInspectionItemVideosById(editingId, Number(it.id));
                const saved = (vids ?? []).map((v: any) => ({
                  id: String(v.id!),
                  url: v.url!,
                  thumbnailUrl: v.thumbnailUrl ?? undefined,
                  contentType: v.contentType ?? undefined,
                })) as SavedVideo[];
                rec.videos = saved;
              } catch { /* ignore */ }
            })
          );

          setInspection(prev => ({
            ...prev,
            ...base,
            records: ensureRecords(sortedItems, ensured),
          }));
        } else {
          // New inspection: just ensure empty records
          setInspection(prev => ({
            ...prev,
            records: ensureRecords(sortedItems, prev.records),
          }));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [editingId, insClient, user?.dealerId]);

  const updateRecord = useCallback(
    (inspectionItemId: ID, patch: Partial<InspectionItemRecord>) => {
      setInspection((prev) => {
        const current = prev.records[inspectionItemId] ?? { inspectionItemId, photos: [], videos: [] };
        return {
          ...prev,
          records: {
            ...prev.records,
            [inspectionItemId]: { ...current, ...patch },
          },
        };
      });
    },
    []
  );

  const canPublish = useMemo(() => !!inspection.vehicle?.id, [inspection.vehicle]);

  const preview = async () => {
    window.open(`/dvi/view/${inspection.id}?preview=true`, "_blank");
  };

  const copyViewerLink = async () => {
    if (!viewerUrl) return;
    try {
      await navigator.clipboard.writeText(viewerUrl);
      alert("Link copied to clipboard.");
    } catch {
      alert("Could not copy link.");
    }
  };

  const downloadQrSvg = () => {
    const svg =
      qrModalRef.current?.querySelector("svg") ??
      qrInlineRef.current?.querySelector("svg");
    if (!svg) return;

    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);
    const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dvi-${inspection.id}-qr.svg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Upload any new PHOTOS (requires inspectionId)
  const uploadNewPhotos = useCallback(async (inspectionIdNum: number) => {
    for (const rec of Object.values(inspection.records)) {
      const itemIdNum = Number(rec.inspectionItemId);
      const newOnes = (rec.photos ?? []).filter(p => "isNew" in p) as NewPhoto[];
      if (newOnes.length === 0) continue;

      const savedBatch: SavedPhoto[] = [];
      for (const p of newOnes) {
        const fileParam = { data: p.file, fileName: p.file.name } as any;
        const dto = await insClient.uploadInspectionPhoto(inspectionIdNum, itemIdNum, fileParam);
        if (p.url) { try { URL.revokeObjectURL(p.url); } catch { } }
        savedBatch.push({
          id: String(dto.id!),
          url: dto.url!,
          thumbnailUrl: dto.thumbnailUrl ?? undefined,
        });
      }

      setInspection(prev => {
        const r = prev.records[String(itemIdNum)];
        const keepSaved = (r.photos ?? []).filter(x => !("isNew" in x)) as SavedPhoto[];
        return {
          ...prev,
          records: {
            ...prev.records,
            [String(itemIdNum)]: { ...r, photos: [...keepSaved, ...savedBatch] },
          }
        };
      });
    }
  }, [inspection.records, insClient]);

  // Upload any new VIDEOS (requires inspectionId)
  const uploadNewVideos = useCallback(async (inspectionIdNum: number) => {
    // @ts-ignore (generated client should have uploadInspectionVideo)
    const uploader: (inspectionId: number, inspectionItemId: number, fp: FileParameter) => Promise<any> =
      (insClient as any).uploadInspectionVideo?.bind(insClient);

    if (!uploader) return; // graceful if client not regenerated yet

    for (const rec of Object.values(inspection.records)) {
      const itemIdNum = Number(rec.inspectionItemId);
      const newOnes = (rec.videos ?? []).filter(v => "isNew" in v) as NewVideo[];
      if (newOnes.length === 0) continue;

      const savedBatch: SavedVideo[] = [];
      for (const v of newOnes) {
        const fp: FileParameter = { data: v.file, fileName: v.file.name } as any;
        const dto = await uploader(inspectionIdNum, itemIdNum, fp);
        if (v.url) { try { URL.revokeObjectURL(v.url); } catch { } }
        savedBatch.push({
          id: String(dto.id!),
          url: dto.url!,
          thumbnailUrl: dto.thumbnailUrl ?? undefined,
          contentType: dto.contentType ?? undefined,
        });
      }

      setInspection(prev => {
        const r = prev.records[String(itemIdNum)];
        const keepSaved = (r.videos ?? []).filter(x => !("isNew" in x)) as SavedVideo[];
        return {
          ...prev,
          records: {
            ...prev.records,
            [String(itemIdNum)]: { ...r, videos: [...keepSaved, ...savedBatch] },
          }
        };
      });
    }
  }, [inspection.records, insClient]);

  // Delete a saved photo on the server
  const deleteSavedPhoto = useCallback(async (photoId: string) => {
    if (!inspection.id) return;
    await insClient.deleteInspectionPhoto(Number(inspection.id), Number(photoId));
  }, [insClient, inspection.id]);

  // Delete a saved video on the server
  const deleteSavedVideo = useCallback(async (videoId: string) => {
    if (!inspection.id) return;
    // @ts-ignore (generated client should have deleteInspectionVideo)
    const deleter = (insClient as any).deleteInspectionVideo?.bind(insClient);
    if (!deleter) return;
    await deleter(Number(inspection.id), Number(videoId));
  }, [insClient, inspection.id]);

  // Actions
  const doSave = async (publish = false) => {
    setSaving(true);
    const client = new InspectionsClient();

    if (!inspection.vehicle?.id) {
      alert("Please select a vehicle before saving.");
      setSaving(false);
      return;
    }

    let newId: ID | undefined = inspection.id;

    if (editingId) {
      const cmd = toUpdateInspectionCommand(
        user?.dealerId,
        editingId,
        Number(inspection.vehicle.id),
        inspection.records,
        inspection.notes ?? null,
        inspection.dealerWebsiteLink ?? null,
        publish ? 1 : 0
      );

      const updated = await client.updateInspection(editingId, cmd);
      newId = String(editingId);
      setInspection((prev) => ({
        ...prev,
        id: newId,
        status: updated.status ?? prev.status,
        savedAt: updated.lastModified ? (updated.lastModified as any).toISOString?.() ?? new Date().toISOString() : new Date().toISOString(),
      }));
    } else {
      const cmd = toCreateInspectionCommand(
        user?.dealerId,
        Number(inspection.vehicle.id),
        inspection.records,
        inspection.notes ?? null,
        inspection.dealerWebsiteLink ?? null,
        publish ? 1 : 0
      );
      const created = await client.createInspection(cmd);
      newId = created && (created as any).id ? String((created as any).id) : undefined;
      setInspection((prev) => ({
        ...prev,
        id: newId ?? prev.id,
        savedAt: created.created ? (created.created as any).toISOString?.() ?? new Date().toISOString() : new Date().toISOString(),
      }));
    }

    // Upload media (requires id)
    if (newId) {
      try {
        await uploadNewPhotos(Number(newId));
      } catch {
        alert("Some photos failed to upload.");
      }
      try {
        await uploadNewVideos(Number(newId));
      } catch {
        alert("Some videos failed to upload.");
      }
      setShowQrModal(true);
    }
    setSaving(false);
  };

  const doDelete = async () => {
    if (!editingId) return;
    setDeleteBusy(true);
    try {
      const client = new InspectionsClient();
      await client.deleteInspection(editingId);
      navigate(-1);
    } catch (err) {
      console.error(err);
      alert("Failed to delete the inspection.");
    } finally {
      setDeleteBusy(false);
      setShowDeleteConfirm(false);
    }
  };

  // Helper: items by category
  const itemsByCategory = useMemo(() => {
    const map = new Map<number, InspectionItem[]>();
    for (const it of items) {
      if (!map.has(it.categoryId)) map.set(it.categoryId, []);
      map.get(it.categoryId)!.push(it);
    }
    return map;
  }, [items]);

  // Show the test UI if you're in dev or you add ?test=1 to the URL
  const isTestUi =
    process.env.NODE_ENV !== "production" ||
    new URLSearchParams(window.location.search).has("test");

  // Randomize all items (statuses, optional notes)
  const randomizeAll = useCallback(() => {
    setInspection(prev => {
      const next: Inspection = { ...prev, records: { ...prev.records } };

      for (const it of items) {
        const current = next.records[it.id] ?? { inspectionItemId: it.id, photos: [], videos: [] };
        const status = weightedRandomStatus();
        const note =
          it.allowsNote && Math.random() < 0.25
            ? randomNoteFor(status)
            : current.note;

        next.records[it.id] = {
          ...current,
          status,
          note,
        };
      }

      return next;
    });
  }, [items, setInspection]);

  const resetScanCount = async () => {
    if (!inspection.id) return;
    const client = new InspectionsClient();
    await client.resetScanCount(Number(inspection.id));
    setInspection((prev) => ({
      ...prev,
      scanCount: 0,
    }));
    setShowResetConfirm(false);
  };

  // Temporary key combo: Ctrl/Cmd + Alt + R
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isR = e.key.toLowerCase() === "r";
      if ((e.ctrlKey || e.metaKey) && e.altKey && isR) {
        e.preventDefault();
        randomizeAll();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [randomizeAll]);

  return (
    <div className="container py-3">
      <Row className="mb-3 align-items-center">
        <Col>
          <h3 className="mb-0">{editingId ? "Edit Inspection" : "New Inspection"}</h3>
          {inspection.savedAt && <div className="text-muted small">Saved {new Date(inspection.savedAt).toLocaleString()}</div>}
          <div className="text-muted small">Status: {inspection.status === 1 ? "Published" : "Draft"}</div>
          <div className="text-muted small">Scan Count: {inspection.scanCount} {inspection.scanCount > 0 && <Button variant="link" className="p-0 m-0 align-baseline" onClick={() => setShowResetConfirm(true)}>reset</Button>} </div>
        </Col>
        <Col className="text-end d-flex gap-2 justify-content-end">
          {isTestUi && (
            <Button
              variant="outline-warning"
              onClick={randomizeAll}
              title="Ctrl/Cmd + Alt + R"
            >
              Randomize (test)
            </Button>
          )}

          {editingId && (
            <Button
              variant="outline-danger"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete
            </Button>
          )}
          {/* {inspection.id && (
            <Button variant="outline-primary" onClick={() => setShowQrModal(true)}>
              Show QR
            </Button>
          )} */}
          <Button variant="secondary" onClick={preview} disabled={!inspection.id}>Preview</Button>
          <Button variant="outline-secondary" disabled={!canPublish} onClick={() => doSave(false)}>Save Draft</Button>
          <Button disabled={!canPublish} onClick={() => doSave(true)}>Publish</Button>
        </Col>
      </Row>

      {/* Persistent QR section */}
      {inspection.id && viewerUrl && (
        <Card className="mb-3">
          <Card.Body className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              <div ref={qrInlineRef}>
                <QR url={viewerUrl} showExtras={false} sizeOverride={150} />
              </div>
              <div>
                <div className="mt-2 d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" onClick={copyViewerLink}>Copy link</Button>
                  <Button size="sm" variant="outline-secondary" onClick={downloadQrSvg}>Download QR (SVG)</Button>
                </div>
              </div>
            </div>
            <div>
              <Button variant="primary" onClick={() => setShowQrModal(true)}>Open Full QR</Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Vehicle picker */}
      <VehiclePicker
        value={inspection.vehicle ?? null}
        onChange={(v) => setInspection((prev) => ({ ...prev, vehicle: v }))}
      />

      <Row>
        <Col md={6} className="">
          {/* Vehicle History Report (PDF) */}
          <Card className="mb-3">
            <Card.Header>Vehicle History Report (PDF)</Card.Header>
            <Card.Body className="d-flex flex-column gap-2">
              {!inspection.id ? (
                <div className="text-muted">
                  Save the inspection first to upload a vehicle history report.
                </div>
              ) : (
                <>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex flex-column">
                      {inspection.vehicleHistoryReportUrl ? (
                        <>
                          <a
                            href={inspection.vehicleHistoryReportUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View current report
                          </a>
                          <div className="small text-muted">
                            {inspection.vehicleHistoryReportUploaded
                              ? `Uploaded ${new Date(inspection.vehicleHistoryReportUploaded).toLocaleString()}`
                              : "Uploaded"}
                            {inspection.vehicleHistoryReportUploadedBy
                              ? ` by ${inspection.vehicleHistoryReportUploadedBy}`
                              : ""}
                          </div>
                        </>
                      ) : (
                        <div className="text-muted">No report uploaded.</div>
                      )}
                    </div>

                    <div className="d-flex gap-2">
                      <input
                        ref={vhrInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={handleVhrFileChange}
                        style={{ display: "none" }}
                      />
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={openVhrPicker}
                        disabled={vhrBusy}
                      >
                        {inspection.vehicleHistoryReportUrl ? "Replace PDF" : "Upload PDF"}
                      </Button>
                      {inspection.vehicleHistoryReportUrl && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={handleVhrDelete}
                          disabled={vhrBusy}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="small text-muted">
                    Accepted file type: PDF. Max size 20MB.
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="">
          {/* Vehicle Service Report (PDF) */}
          <Card className="mb-3">
            <Card.Header>Vehicle Service History (PDF)</Card.Header>
            <Card.Body className="d-flex flex-column gap-2">
              {!inspection.id ? (
                <div className="text-muted">
                  Save the inspection first to upload a vehicle service report.
                </div>
              ) : (
                <>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex flex-column">
                      {inspection.vehicleServiceRecordUrl ? (
                        <>
                          <a
                            href={inspection.vehicleServiceRecordUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View current report
                          </a>
                          <div className="small text-muted">
                            {inspection.vehicleServiceRecordUploaded
                              ? `Uploaded ${new Date(inspection.vehicleServiceRecordUploaded).toLocaleString()}`
                              : "Uploaded"}
                            {inspection.vehicleServiceRecordUploadedBy
                              ? ` by ${inspection.vehicleServiceRecordUploadedBy}`
                              : ""}
                          </div>
                        </>
                      ) : (
                        <div className="text-muted">No report uploaded.</div>
                      )}
                    </div>

                    <div className="d-flex gap-2">
                      <input
                        ref={vsrInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={handleVsrFileChange}
                        style={{ display: "none" }}
                      />
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={openVsrPicker}
                        disabled={vsrBusy}
                      >
                        {inspection.vehicleServiceRecordUrl ? "Replace PDF" : "Upload PDF"}
                      </Button>
                      {inspection.vehicleServiceRecordUrl && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={handleVsrDelete}
                          disabled={vsrBusy}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="small text-muted">
                    Accepted file type: PDF. Max size 20MB.
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Header>Dealer Website Link</Card.Header>
        <Card.Body>
          <Form.Label htmlFor="dealer-website-link" className="mb-2">Dealer Website Link</Form.Label>
          <Form.Control
            id="dealer-website-link"
            type="url"
            placeholder="Enter the dealer website link"
            value={inspection.dealerWebsiteLink ?? ""}
            onChange={(e) =>
              setInspection(prev => ({ ...prev, dealerWebsiteLink: e.target.value }))
            }
          />
        </Card.Body>
      </Card>

      {/* Render by Category */}
      {loading ? (
        <div className="text-muted">Loading…</div>
      ) : (
        categories.map(cat => {
          const catItems = itemsByCategory.get(cat.id!) ?? [];
          if (catItems.length === 0) return null;
          return (
            <Card key={cat.id} className="mb-4">
              <Card.Header>{cat.name}</Card.Header>
              <Card.Body className="pt-3">
                {catItems.map(i => (
                  <ItemRow
                    key={i.id}
                    item={i}
                    record={inspection.records[i.id]}
                    onChange={(patch) => updateRecord(i.id, patch)}
                    onDeleteSavedPhoto={async (photoId) => {
                      await deleteSavedPhoto(photoId);
                    }}
                    onDeleteSavedVideo={async (videoId) => {
                      await deleteSavedVideo(videoId);
                    }}
                  />
                ))}
              </Card.Body>
            </Card>
          );
        })
      )}

      <Card className="mb-4">
        <Card.Header>Additional Comments</Card.Header>
        <Card.Body>
          <Form.Label htmlFor="inspection-notes" className="mb-2">Anything else to note?</Form.Label>
          <Form.Control
            id="inspection-notes"
            as="textarea"
            rows={4}
            placeholder="Add any additional comments about this vehicle or inspection…"
            value={inspection.notes ?? ""}
            onChange={(e) =>
              setInspection(prev => ({ ...prev, notes: e.target.value }))
            }
          />
        </Card.Body>
      </Card>

      <div className="d-flex gap-2 justify-content-end">
        <Button disabled={!canPublish} variant="outline-secondary" onClick={() => doSave(false)}>Save Draft</Button>
        <Button disabled={!canPublish} onClick={() => doSave(true)}>Publish</Button>
      </div>

      {/* QR Modal */}
      <Modal show={showQrModal} onHide={() => setShowQrModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Inspection QR Code</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {viewerUrl ? (
            <>
              <div className="d-flex justify-content-center mb-3" ref={qrModalRef}>
                <QR url={viewerUrl} showExtras={true} sizeOverride={220} />
              </div>
              <div className="small text-break">{viewerUrl}</div>
            </>
          ) : (
            <div className="text-muted">Save the inspection to generate a link.</div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={copyViewerLink} disabled={!viewerUrl}>Copy link</Button>
            <Button variant="outline-secondary" onClick={downloadQrSvg} disabled={!viewerUrl}>Download QR (SVG)</Button>
            <Button variant="primary" onClick={() => setShowQrModal(false)}>Done</Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Delete confirm */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => (!deleteBusy && setShowDeleteConfirm(false))}
        centered
        backdrop={deleteBusy ? "static" : true}
        keyboard={!deleteBusy}
      >
        <Modal.Header closeButton={!deleteBusy}>
          <Modal.Title>Delete Inspection</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="fw-semibold">Are you sure, this cannot be undone</div>
          <div className="text-muted small mt-2">
            This will permanently remove the inspection and its associated records/media.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteConfirm(false)}
            disabled={deleteBusy}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={doDelete}
            disabled={deleteBusy}
          >
            {deleteBusy ? "Deleting..." : "Delete Inspection"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reset scan count */}
      <Modal
        show={showResetConfirm}
        onHide={() => (!resetBusy && setShowResetConfirm(false))}
        centered
        backdrop={resetBusy ? "static" : true}
        keyboard={!resetBusy}
      >
        <Modal.Header closeButton={!resetBusy}>
          <Modal.Title>Reset Scan Count</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="fw-semibold">Are you sure, this cannot be undone</div>
          <div className="text-muted small mt-2">
            This will permanently reset the scan count for this inspection.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowResetConfirm(false)}
            disabled={resetBusy}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={resetScanCount}
            disabled={resetBusy}
          >
            {resetBusy ? "Resetting..." : "Reset Scan Count"}
          </Button>
        </Modal.Footer>
      </Modal>

      {saving && <LoaderCover />}
    </div>
  );
};

export default DviEditor;
